import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { isEmpty } from "lodash";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { CandidateSummary, MasterData, OBAttempt, Obserevations, ObserevationsResp } from "src/app/models/models";
import { GoogleAuthService } from "src/app/services/google-api.service";
import { enrichMasterDataWithAocCatalog } from "src/app/shared/utils/master-catalog.utils";
import { buildAocFilterOptions, filterOptionsForAoc } from "src/app/util/aoc-filter-options.utils";
import { sortByCodeOrder } from "src/app/util/sort-by-code-order";
import { SESSION } from "src/session.config";
import { CbtaModalComponent } from "../components/cbta-modal/cbta-modal.component";
import { LoaderService } from "./loader.service";
import { RoleService } from "./role.service";

@Injectable({
	providedIn: "root",
})
export class SharedService {
	private activePageName$ = new BehaviorSubject<string>("");
	private quickSummary$ = new Subject<CandidateSummary[]>();
	private sideBarIsExpanded$ = new Subject<boolean>();
	errorMessages: any = CONSTANT.ERROR_MESSAGES;

	private isOnline$ = new Subject<boolean>();

	constructor(
		private dialog: MatDialog,
		public readonly authService: GoogleAuthService,
		private roleService: RoleService,
		private http: HttpClient,
		private loaderService: LoaderService,
	) {}

	setIsOnline(isOnline: boolean) {
		this.isOnline$.next(isOnline);
	}

	getIsStatusOnline(): Observable<boolean> {
		return this.isOnline$.asObservable();
	}

	getIsOnline() {
		return navigator.onLine;
	}

	setActivePage(activePage: string) {
		this.activePageName$.next(activePage);
	}

	getActivePage(): Observable<any> {
		return this.activePageName$.asObservable();
	}

	setSideBarExpanded(sideBarIsExpanded: boolean) {
		this.sideBarIsExpanded$.next(sideBarIsExpanded);
	}

	getSideBarExpanded(): Observable<boolean> {
		return this.sideBarIsExpanded$.asObservable();
	}

	setQuickSummary(candidateList: CandidateSummary[]) {
		this.quickSummary$.next(candidateList);
	}

	getQuickSummary(): Observable<CandidateSummary[]> {
		return this.quickSummary$.asObservable();
	}

	switchRoles(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "switch-role-modal",
			height: "280px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalName: "switchRoleModal",
				modalTitle: "Switch Role",
				modalContent: "",
				noAction: true,
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (!isEmpty(result?.role)) {
				this.roleService.switchRole(result.role);
			}
			sub.unsubscribe();
		});
	}

	logout(): void {
		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "200px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Logout",
				modalContent: "Are you sure you want to logout?",
				modalBtnConfirmText: "Yes",
				modalBtnCancelText: "No",
			},
		});

		const sub = dialogRef.afterClosed().subscribe((result: any) => {
			if (result && result.close) {
				void this.completeExplicitLogout();
			}
			sub.unsubscribe();
		});
	}

	/**
	 * Explicit logout: only clears local state after server logout succeeds.
	 * If offline or logout fails, keep the session so the user is not stranded mid-flight.
	 */
	private async completeExplicitLogout(): Promise<void> {
		const ok = await this.authService.safeSignOut();
		if (ok) {
			localStorage.clear();
			return;
		}
		this.dialog.open(CbtaModalComponent, {
			panelClass: "logout-modal",
			height: "220px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalTitle: "Logout pending",
				modalContent:
					"Couldn't reach the server. You're still signed in on this device — logout will complete when you're stably online. Try again when connected.",
				modalBtnConfirmText: "OK",
				modalBtnCancelText: "Close",
			},
		});
	}

	getErrorMessage(control: AbstractControl): string {
		if (control.errors && control.errors["customUserEmail"]) {
			const error = control.errors["customUserEmail"];
			return `Email must be from ${error.requiredDomain} domain`;
		}

		for (const errorType in control.errors) {
			if (control.errors.hasOwnProperty(errorType) && control.errors[errorType]) {
				return this.errorMessages[errorType];
			}
		}
		return "";
	}

	/** User-friendly message for staff number lookup failures (404/500/not found). */
	resolveStaffNumberLookupMessage(error: unknown): string {
		if (error == null) {
			return "Unable to retrieve staff details. Please check the staff number and try again.";
		}

		if (typeof error === "string") {
			const lower = error.toLowerCase();
			if (
				lower.includes("no profile found") ||
				lower.includes("not found") ||
				lower.includes("http failure") ||
				lower.includes("404") ||
				lower.includes("500")
			) {
				return "User not found. Please check the staff number and try again.";
			}
			return error;
		}

		const err = error as Record<string, unknown>;
		const nested = err["error"];
		const parts: string[] = [];

		if (typeof err["message"] === "string") parts.push(err["message"]);
		if (typeof err["statusCode"] === "number") parts.push(String(err["statusCode"]));
		if (typeof err["status"] === "number") parts.push(String(err["status"]));
		if (Array.isArray(err["errors"])) parts.push(err["errors"].map(String).join(" "));
		if (typeof nested === "string") {
			parts.push(nested);
		} else if (nested && typeof nested === "object") {
			const n = nested as Record<string, unknown>;
			if (typeof n["message"] === "string") parts.push(n["message"]);
			if (typeof n["error"] === "string") parts.push(n["error"]);
			if (typeof n["statusCode"] === "number") parts.push(String(n["statusCode"]));
			if (Array.isArray(n["errors"])) parts.push(n["errors"].map(String).join(" "));
		}

		const haystack = parts.join(" ").toLowerCase();
		if (
			haystack.includes("no profile found") ||
			haystack.includes("not found") ||
			haystack.includes("user not found")
		) {
			return "User not found. Please check the staff number and try again.";
		}

		const status = (err["status"] as number | undefined) ?? (err["statusCode"] as number | undefined);
		if (status === 404 || status === 500) {
			return "User not found. Please check the staff number and try again.";
		}

		return "Unable to retrieve staff details. Please check the staff number and try again.";
	}

	showStaffLookupErrorModal(message: string, title = "Staff number not found"): void {
		const content =
			typeof message === "string" && message.trim()
				? message
				: "User not found. Please check the staff number and try again.";

		this.dialog.open(CbtaModalComponent, {
			panelClass: ["error-modal", "staff-lookup-error-modal"],
			height: "220px",
			width: "360px",
			hasBackdrop: true,
			data: {
				modalName: "error-modal",
				modalTitle: title,
				modalContent: content,
				modalBtnConfirmText: "Close",
				noCloseBtn: true,
			},
		});
	}

	filterOptions: { label: string; value: string }[] = [
		{
			label: "eTS-1: Flight Crew Training ",
			value: "ts_1",
		},
		{
			label: "eTS-3 :  IEC",
			value: "ets_3",
		},
		{
			label: "eTS-5 : TODA ",
			value: "ets_5",
		},
		{
			label: "eTS-6 : Training Review",
			value: "ets_6",
		},
		{
			label: "Cabin Crew",
			value: "cabin_crew",
		},
		{
			label: "Cabin Crew Instructor",
			value: "cabin_crew_instructor",
		},
	];

	/** Form types eligible for feedback template attachment (excludes eTS-5/eTS-6). */
	get feedbackAttachableFilterOptions(): { label: string; value: string }[] {
		return this.filterOptions.filter((option) => option.value !== "ets_5" && option.value !== "ets_6");
	}

	get FielderrorMessages() {
		return {
			required: "This field is required",
			minlength: "Minimum length is 3 characters",
			pattern: "Please enter a valid value.",
		};
	}

	get aocs() {
		return this.sessionMasterData?.aocs;
	}

	get aocFilterOptions() {
		return buildAocFilterOptions(this.sessionMasterData?.aocs);
	}

	getFilterOptionsForAoc(aocId?: string): { label: string; value: string }[] {
		return filterOptionsForAoc(this.filterOptions, aocId, this.sessionMasterData?.aocs);
	}

	get dropDownFormTypeOptions() {
		const formTypes = this.sessionMasterData?.formTypes;
		if (!formTypes?.length) {
			return formTypes;
		}

		return sortByCodeOrder(
			formTypes,
			this.filterOptions.map((option) => option.value),
		);
	}

	get dropDownOptionsManeuver() {
		return this.sessionMasterData.maneuvers ?? [];
	}

	get dropDownOptionsRank() {
		return this.sessionMasterData?.ranks?.filter((d) => d.code !== "cpts");
	}

	get dropDownOptionsLicensTypes() {
		return this.sessionMasterData?.licenseTypes ?? [];
	}

	/**
	 * Get filtered rank options based on form type.
	 * ETS-5: Captain, First Officer, Second Officer, N/A (N/A last).
	 * ETS-6: Captain, First Officer, Senior First Officer, Second Officer only.
	 * ETS-3: those plus CC and SCC (CC/SCC listed last).
	 * Cabin Crew / Cabin Crew Instructor: PUR, SCC, CC, N/A.
	 * Other forms: all ranks.
	 */
	getFilteredRankOptions(formType?: string) {
		const opts = this.dropDownOptionsRank ?? [];

		const allowedPilotRankCodes = new Set(["captain", "fo", "sfo", "so"]);
		const allowedCabinCrewRankCodes = new Set(["pur", "scc", "cc", "na"]);
		const ets3AllowedRankCodes = new Set(["captain", "fo", "sfo", "so", "scc", "cc"]);
		const isNaRank = (opt: any) => {
			const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
			return code === "na" || code === "n/a";
		};

		const isTS3 = formType === CONSTANT.FORM_TYPE.ts_3;
		const isTS5 = formType === CONSTANT.FORM_TYPE.ts_5;
		const isTS6 = formType === CONSTANT.FORM_TYPE.ts_6;

		if (isTS5) {
			const allowedTS5RankCodes = new Set(["captain", "fo", "sfo", "so", "na"]);
			const filtered = opts.filter((opt: any) => {
				const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
				return allowedTS5RankCodes.has(code);
			});
			const withoutNa = filtered.filter((o) => !isNaRank(o));
			const naOpt = filtered.find(isNaRank) ?? { code: "na", description: "N/A" };
			return [...withoutNa, naOpt];
		}

		if (isTS6) {
			return opts.filter((opt: any) => {
				const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
				return allowedPilotRankCodes.has(code);
			});
		}

		const isCabinCrew = formType === CONSTANT.FORM_TYPE.cabin_crew;
		const isCabinCrewInstructor = formType === CONSTANT.FORM_TYPE.cabin_crew_instructor;

		if (isCabinCrew || isCabinCrewInstructor) {
			return opts.filter((opt: any) => {
				const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
				return allowedCabinCrewRankCodes.has(code);
			});
		}

		if (isTS3) {
			const filtered = opts.filter((opt: any) => {
				const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
				return ets3AllowedRankCodes.has(code);
			});

			const moveToEnd = new Set(["cc", "scc"]);
			const normal: any[] = [];
			const tail: any[] = [];
			for (const opt of filtered) {
				const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
				(moveToEnd.has(code) ? tail : normal).push(opt);
			}
			return [...normal, ...tail];
		}

		return opts;
	}

	get dropDownOptionsAircraftType() {
		return [
			{
				code: "A330",
				description: "A330",
			},
			{
				code: "A320",
				description: "A320",
			},
		];
	}

	get dropdownOtionsLicenseType() {
		return this.sessionMasterData?.licenseTypes ?? [];
	}

	/** License Type options; ETS-3 only: ensures N/A exists and is last. */
	getLicenseTypeOptions(formType?: string) {
		const base = Array.isArray(this.sessionMasterData?.licenseTypes) ? this.sessionMasterData.licenseTypes.slice() : [];

		if (formType !== CONSTANT.FORM_TYPE.ts_3) return base;

		const isNaOpt = (opt: any) => {
			const code = ((opt?.code ?? opt?.value ?? opt) + "").toLowerCase().trim();
			return code === "na" || code === "n/a";
		};

		const withoutNa = base.filter((o) => !isNaOpt(o));
		const existingNa = base.find((o) => isNaOpt(o)) ?? { code: "na", description: "N/A" };

		return [...withoutNa, existingNa];
	}

	get dropDownOptionsPilotProfile() {
		return this.sessionMasterData?.trainingProfiles;
	}

	get dropdownOptionsPilotFolderCategories() {
		return this.sessionMasterData?.documentTypes;
	}

	get sessionMasterData() {
		if (localStorage.getItem(SESSION.SESSION_KEY_MASTER_DATA)) {
			const raw = JSON.parse(localStorage.getItem(SESSION.SESSION_KEY_MASTER_DATA)!) as MasterData;
			return enrichMasterDataWithAocCatalog(raw) ?? ({} as MasterData);
		} else {
			return {} as MasterData;
		}
	}

	get formMasterData() {
		if (localStorage.getItem(SESSION.SESSION_KEY_FORM_MASTER_DATA)) {
			return JSON.parse(localStorage.getItem(SESSION.SESSION_KEY_FORM_MASTER_DATA)!) as any;
		} else {
			return {} as any;
		}
	}

	get allSessionConfigs() {
		return this.sessionMasterData?.sessions;
	}

	get observations(): Obserevations[] {
		return this.sessionMasterData?.observations;
	}

	// Function to group observations by observation category code
	groupObservationsByCategory(observations: ObserevationsResp[]) {
		const result = {} as any;
		observations.forEach((item: ObserevationsResp) => {
			const { description } = item.category;
			const attempt = item.attempt;

			if (!result[description]) {
				result[description] = [];
			}
			result[description].push({
				code: item.code,
				name: item.code.toLocaleUpperCase().replace("_", ""),
				attempt: item.attempt,
			});
		});
		return result;
	}

	get dropDownOptionsSecNum() {
		let a = [{ code: "na", description: "N/A" }];
		const aList = Array.from({ length: 150 }, (_, i) => {
			return {
				code: `${i + 1}`,
				description: `${i + 1}`,
			};
		});

		return a.concat(aList);
	}

	generateCategoryObj(): any {
		const result: { [key: string]: { [key: string]: OBAttempt } } = {};
		const msd = localStorage.getItem(SESSION.SESSION_KEY_MASTER_DATA);
		if (!msd) return null;

		// if (msd) {
		const js_msd = JSON.parse(msd);

		const catObjDefault = {
			currentCount: 0,
			preDefined: false,
			totalCount: 0,
			lastAction: CONSTANT.OB_LAST_ACTION.NO_ACTION,
		};

		// Iterate through the observationList
		js_msd.observations.forEach((item: any) => {
			const category = item.observation_category.description; // e.g., 'COM'
			const itemName = item.name.replace(" ", ""); // e.g., 'COM1'

			// Initialize the category if it doesn't exist
			if (!result[category]) {
				result[category] = {};
			}

			result[category][itemName] = catObjDefault;
		});

		const categoryCounts: { [key: string]: number } = {};
		Object.keys(result).forEach((category) => {
			categoryCounts[category] = Object.keys(result[category]).length;
		});
		// }

		Object.keys(categoryCounts).forEach((category) => {
			if (!result[category]) {
				result[category] = {};
			}

			for (let i = 1; i <= categoryCounts[category]; i++) {
				const itemName = `${category}${i}`;
				result[category][itemName] = catObjDefault;
			}
		});

		return result;
	}

	generateSortedOBList(formType = CONSTANT.FORM_TYPE.ts_1): any {
		// Helpers
		const safeParse = <T = any>(json: string | null): T | null => {
			if (!json) return null;
			try {
				return JSON.parse(json) as T;
			} catch {
				return null;
			}
		};

		const isCabinCrewForm =
			formType === CONSTANT.FORM_TYPE.cabin_crew || formType === CONSTANT.FORM_TYPE.cabin_crew_instructor;

		// Load observations list (js_msd) based on form type
		let js_msd: any[] | null = null;

		if (formType === CONSTANT.FORM_TYPE.no_form) {
			const masterRaw = localStorage.getItem(SESSION.SESSION_KEY_MASTER_DATA);
			const master = safeParse<any>(masterRaw);
			if (!master) return null;
			js_msd = master;
		} else {
			const formMasterRaw = localStorage.getItem(SESSION.SESSION_KEY_FORM_MASTER_DATA);
			const masterRaw = localStorage.getItem(SESSION.SESSION_KEY_MASTER_DATA);
			const formMaster = safeParse<any>(formMasterRaw);
			const master = safeParse<any>(masterRaw);

			if (!formMaster || !master) return null;

			const masterConfig = formMaster?.[formType] ?? null;
			if (!masterConfig?.observations || !Array.isArray(masterConfig.observations)) return null;

			const codeUsed: string[] = [];
			masterConfig.observations.forEach((o: any) => codeUsed.push(o.code));

			// master.observations expected as array
			const masterObs = Array.isArray(master?.observations) ? master.observations : [];
			js_msd = masterObs.filter((o: any) => codeUsed.includes(o.code));
		}

		if (!js_msd || !Array.isArray(js_msd)) return null;

		// Sort the list before partitioning
		js_msd.sort((a: any, b: any) => {
			if (isCabinCrewForm) {
				const [aCode, aPrefix, aNum] = String(a.code).split("_");
				const [bCode, bPrefix, bNum] = String(b.code).split("_");
				if (aPrefix < bPrefix) return -1;
				if (aPrefix > bPrefix) return 1;
				return (parseInt(aNum, 10) || 0) - (parseInt(bNum, 10) || 0);
			} else {
				const [aPrefix, aNum] = String(a.code).split("_");
				const [bPrefix, bNum] = String(b.code).split("_");
				if (aPrefix < bPrefix) return -1;
				if (aPrefix > bPrefix) return 1;
				return (parseInt(aNum, 10) || 0) - (parseInt(bNum, 10) || 0);
			}
		});

		// Build category map
		const categoryMap: Record<string, Record<string, any[]>> = {};

		js_msd.forEach((item: any) => {
			const desc = item?.observation_category?.sub_description ?? "";

			let title = "";
			if (isCabinCrewForm) {
				const [aCode, aPrefix] = String(item.code).split("_");
				title = `${aCode}_${aPrefix}`.toUpperCase();
			} else {
				const [aPrefix] = String(item.code).split("_");
				title = (aPrefix || "").toUpperCase();
			}

			categoryMap[title] ||= {};
			categoryMap[title][desc] ||= [];
			categoryMap[title][desc].push({
				description: item.description,
				name: item.name,
			});
		});

		// Flatten into final form
		const formObj = Object.entries(categoryMap)
			.map(([title, subCategories]) => {
				const [desc, item_desc] = Object.entries(subCategories)[0] as [string, any[]];

				const sortKey = title === CONSTANT.SORTING_ORDER.ONE ? 1 : title === CONSTANT.SORTING_ORDER.TWO ? 2 : 3;

				const name = isCabinCrewForm ? (title.split("_")[1] || "").toUpperCase() : title;

				return { title, name, desc, item_desc, sortKey };
			})
			.sort((a, b) => a.sortKey - b.sortKey);

		return formObj;
	}

	organizeManeuvers(inputOB: any) {
		const categorizedData = {} as any;

		Object.keys(inputOB).forEach((childKey) => {
			const child = inputOB[childKey];

			const category = childKey.split("_")[0].toUpperCase();

			if (!categorizedData[category]) {
				categorizedData[category] = [];
			}

			categorizedData[category].push({
				code: childKey,
				name: childKey.toUpperCase().replace("_", ""),
				isRemoved: child.isRemoved,
				isAdded: child.isAdded,
				activeAttempt: child.activeAttempt,
				totalAttempt: child.totalAttempt,
				isOriginal: child.isOriginal,
			});
		});

		return categorizedData;
	}

	download(pdfUrl: string) {
		this.loaderService.setLoading(true);
		this.http.get(pdfUrl, { responseType: "blob" }).subscribe(
			(blob: Blob) => {
				const name = this.getFileNameFromUrl(pdfUrl)?.toUpperCase();

				const link = document.createElement("a");
				link.href = window.URL.createObjectURL(blob);
				link.setAttribute("download", name);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				this.loaderService.setLoading(false);
			},
			(error) => {
				console.error("Error downloading PDF:", error);
				this.loaderService.setLoading(false);
			},
		);
	}

	getFileNameFromUrl = (url: string) => {
		if (url?.length === 0) return "";

		const urlObj = new URL(url);
		const pathname = urlObj.pathname;
		const encodedFileName = pathname.substring(pathname.lastIndexOf("/") + 1);
		return decodeURIComponent(encodedFileName);
	};
}
